import { CodeEditorSegmegment, ReviewComment } from "./CodeEditor";
import { DealStatus } from "./DealStatus";
import { PurchaseType } from "./PurchaseType";
import { Skill } from "./Skill";

/**
 * レビュー取得並び替えタイプ
 */
export type DealSortType =
  | 1 // 新着順
  | 2 // 期限が近い順
  | 3 // 期限が遠い順
  | 4 // 金額が安い順
  | 5; // 金額が高い順

export const dealSortTypes: number[] = [1, 2, 3, 4, 5];

/**
 * レビュー取得用クエリ
 */
export interface GetReviewsRequestParameters {
  /**
   * 並び替え
   */
  sort: DealSortType;

  /**
   * ページインデックス
   */
  page: number;

  /**
   * 金額下限
   */
  amount: number;

  /**
   * 含める取引ステータス(null: [Pending, Completed] が指定される)
   */
  statuses: DealStatus[] | null;

  /**
   * 含める言語(null: 指定しない)
   */
  languages: string[] | null;

  /**
   * タイトル検索キーワード(null: 指定しない)
   */
  keyword: string | null;

  /**
   * スキル制限付きを除外するか
   */
  exclude: boolean;


  /**
   * 閲覧権限
   */
  viewPermissions: string[];
}

/**
 * レビューインターフェース
 * NOTE: 20_レビュー取得（一覧）参照
 */
export interface Review {
  dealId: string;
  title: string;
  createDate: string;
  dealStatus: DealStatus;
  purchaseOverview: string;
  language: string;
  tags: string;
  amount: number;
  publishedPeriod: string;
  purchaseUserId: string;
  purchaseUserIconUrl: string;
  orderSkills: Skill[] | null;
  purchaseType: PurchaseType;
}

/**
 * レビュー一覧レスポンス
 */
export interface GetReviewsResponse {
  totalCount: number;
  results: Review[];
}

/**
 * レビュー詳細レスポンス
 */
export type ReviewResponse = {
  /** 取引 ID */
  dealId: string;
  /** タイトル */
  title: string;
  /** 作成日 */
  createDate: Date;
  /** 取引ステータス */
  dealStatus: DealStatus;
  /** 閲覧権限 */
  viewPermission: string;
  /** 依頼概要 */
  purchaseOverview: string;
  /** 言語 */
  language: string;
  /** タグ */
  tags: string;
  /** 金額 */
  amount: number;
  /** 掲載期限*/
  publishedPeriod: string;
  /** 受注必須スキル */
  orderSkills: Skill[] | null;
  /** コードエディタ内容 */
  codeEditor: CodeEditorSegmegment[];
  /** 依頼者ユーザー ID */
  purchaseUserId: string;
  /** 依頼者アイコン URL */
  purchaseUserIconUrl: string;
  /** 依頼者名 */
  purchaseNickname: string;
  /** 良い評価 */
  goodEvaluation: number;
  /** 悪い評価 */
  badEvaluation: number;
  /** 依頼方法 */
  purchaseType: PurchaseType;
  /** 入札上限有無 */
  purchaseLimit?: PurchaseLimit;
  /** 入札件数 */
  tenderCount: number;
  /** 上限人数 */
  limitCount?: number;
  /** 自動再入札設定 */
  autoTenderType?: AutoTenderType;
  /** 落札の優先度設定 */
  tenderPriority?: TenderPriority;
  /** 受注者入札状況 */
  isTender?: boolean;
  isUseCoupon: boolean;
};

/**
 * レビュー詳細(おすすめ)レスポンス
 */
export type ReviewRecommendResponse = {
  dealId: string;
  title: string;
  createDate: Date;
  purchaseOverview: string;
  language: string;
  amount: number;
  publishedPeriod: string;
  purchaseUserId: string;
  purchaseIconUrl: string;
  viewPermission: string;
};

/**
 * レビュー作成リクエスト
 */
export interface CreateReviewRequest {
  purchaseType: PurchaseType;
  title: string;
  purchaseOverview: string;
  language: string;
  tags?: string;
  amount: number | null;
  publishedPeriod: string | null,
  orderSkills: Skill[] | null;
  codeEditor: CodeEditorSegmegment[];
  purchaseLimit?: PurchaseLimit;
  limitCount?: number;
  autoTenderType?: AutoTenderType;
  tenderPriority?: TenderPriority;
  viewPermission: string;
  isUseCoupon: boolean;
}

/** 入札上限有無タイプ */
export type PurchaseLimit = 
| "1"  // 制限
| "2"; // 無制限

/** 自動再入札設定 */
export type AutoTenderType = 
| "1"  // 設定する
| "2"; // 設定しない


/** 落札の優先度設定 */
export type TenderPriority = 
| "0"  // 未設定
| "1"  // スキルが最も高い
| "2"  // 評価が最も高い
| "3"  // 金額が最も安い
| "4"; // 金額が最も高い

/**
 * レビュー作成レスポンス
 */
export interface CreateReviewResponse {
  dealId: string;
  createDate: Date;
}

/**
 * レビュー決済リクエスト
 */
 export type SettlementReviewRequest =
 | {
     /** クレジットカード入力フラグ */
     cardInputFlg: false;
     /**
      * 依頼方法
      */
     purchaseType: PurchaseType;
     /**
      * 入札 ID
      */
     tenderId?: string;
   }
 /** 保存しているカードを利用 */
 | {
     /** クレジットカード入力フラグ */
     cardInputFlg: true;
     /**
      * カードトークン
      */
     cardToken: string;
     /**
      * 入力カード保存フラグ
      */
     cardSaveFlg: boolean;
     /**
      * 依頼方法
      */
     purchaseType: PurchaseType;
     /**
      * 入札 ID
      */
     tenderId?: string;
   };

/**
 * レビュー決済レスポンス
 */
export interface SettlementReviewResponse {
  dealId: string;
  orderId: string;
  redirectUrl: string;
}

export type ReviewEvaluation =
  | "1" // 良い
  | "2" // 悪い
  | "3"; // 評価なし

/**
 * レビュー更新リクエスト
 */
export interface UpdateReviewRequest {
  purchaseType: PurchaseType;
  title: string;
  purchaseOverview: string;
  language: string;
  tags?: string;
  orderSkills: Skill[] | null;
  codeEditor: CodeEditorSegmegment[];
  amount: number | null;
  publishedPeriod: string | null;
  purchaseLimit?: PurchaseLimit;
  limitCount?: number;
  autoTenderType?: AutoTenderType;
  tenderPriority?: TenderPriority;
  viewPermission: string;
  isUseCoupon: Boolean;
}

/**
 * レビュー更新レスポンス
 */
export interface UpdateReviewResponse {
  dealId: string;
  dealStatus: DealStatus;
  title: string;
  createDate: Date;
  purchaseOverview: string;
  language: string;
  tags?: string;
  amount: number | null;
  publishedPeriod: string;
  orderSkills: Skill[] | null;
  codeEditor: CodeEditorSegmegment[];
  purchaseUserId: string;
  purchaseNickname: string;
  purchaseUserIconUrl: string;
  goodEvaluation: number;
  badEvaluation: number;
  purchaseType: PurchaseType;
}

/**
 * レビューステータス更新リクエスト
 */
export interface UpdateReviewStatusRequest {
  dealStatus: DealStatus;
  orderUserId?: string;
  reviewComment?: ReviewComment[];
  evaluation?: ReviewEvaluation;
}

/**
 * レビューステータス更新レスポンス
 */
export interface UpdateReviewStatusResponse {
  dealId: string;
  purchaseUserId: string;
  purchaseNickname: string;
  purchaseIconUrl: string;
  // 受注者がいない場合は空文字
  orderUserId: string;
  orderNickname: string;
  orderIconUrl: string;
  dealStatus: DealStatus;
  title: string;
  amount?: number;
  dealStartTime: string;
  codeEditor: CodeEditorSegmegment[];
  reviewComment: ReviewComment[];
  purchaseType: string;
  questionLimitType: string;
  questionLimitCount: number;
}

/**
 * レビュー金額取得リクエスト
 */
export interface CommissionRequestDto {
  settlementType: string;
  amount?: number;
  cardToken?: string;
}

/**
 * レビュー金額取得レスポンス
 */
export interface CommissionResponseDto {
  amount: number;
  systemCommission: number;
  settlementCommission: number;
}